import { useCallback, useEffect, useMemo, useState } from 'react';
import { getErrorMessage } from '../utils/errors';
import { formatFileSize } from '../utils/formatters';
import {
  buildRunFileTree,
  countTreeFiles,
  describeCron,
  FILE_OUTCOME_LABEL,
  fileOutcomeColor,
  RUN_STATUS_LABEL,
  runDuration,
  runDurationMs,
  runStatusColor,
  summarizeRunCounts,
  type ExtractionRun,
  type ExtractionRunFile,
  type ExtractionSummary,
  type RunFileTreeNode,
} from '../utils/extractionRuns';
import { ConfirmRunModal } from './ConfirmRunModal';
import './ExtractionRunsView.css';

/** One page of a run's file rows; `next_cursor` null = last page. */
export interface RunFilesPageResult {
  run: ExtractionRun;
  files: ExtractionRunFile[];
  next_cursor: string | null;
}

/** How the host SPA fetches run data (customer app: project-scoped; admin: org-scoped). */
export interface ExtractionRunsSource {
  listRuns(): Promise<{ runs: ExtractionRun[]; ledger_configured: boolean }>;
  getRun(runId: string, cursor?: string): Promise<RunFilesPageResult>;
}

/**
 * Upper bound on rows rendered for one run. A weekly drop is dozens of files;
 * this only guards the browser against a pathological run (the API pages,
 * the ledger has no cap).
 */
export const MAX_RENDERED_FILES = 20000;

interface ExtractionRunsViewProps {
  extraction: ExtractionSummary;
  source: ExtractionRunsSource;
  onBack?: () => void;
  backLabel?: string;
  /** Admin console only: starts a manual run; the view then opens that run. */
  onRunNow?: () => Promise<{ run_id: string }>;
  /** Open this run's detail straight away (e.g. right after triggering it). */
  initialRunId?: string | null;
}

/**
 * Read-only: the list of runs of one extraction (newest first) and, on click,
 * a run's file rows as a folder tree. No action can be taken from here —
 * re-runs and configuration live in the data-extraction repository.
 */
export function ExtractionRunsView({ extraction, source, onBack, backLabel = 'Back', onRunNow, initialRunId }: ExtractionRunsViewProps) {
  const [runs, setRuns] = useState<ExtractionRun[]>([]);
  const [ledgerConfigured, setLedgerConfigured] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRunId, setSelectedRunId] = useState<string | null>(initialRunId ?? null);
  const [starting, setStarting] = useState(false);
  const [confirmingRun, setConfirmingRun] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const maxDurationMs = useMemo(() => runs.reduce((max, r) => Math.max(max, runDurationMs(r) ?? 0), 0), [runs]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await source.listRuns();
      setRuns(res.runs);
      setLedgerConfigured(res.ledger_configured);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [source]);

  useEffect(() => {
    load();
  }, [load]);

  if (selectedRunId) {
    return (
      <RunDetail
        extraction={extraction}
        runId={selectedRunId}
        source={source}
        onBack={() => setSelectedRunId(null)}
      />
    );
  }

  return (
    <div className="extraction-runs">
      <div className="extraction-runs-header">
        <div>
          {onBack && (
            <button type="button" className="btn-link" onClick={onBack}>
              &larr; {backLabel}
            </button>
          )}
          <h3>
            {extraction.name} <span className="status-badge gray extraction-type-badge">{extraction.source_type}</span>
          </h3>
          <p className="extraction-runs-subtitle">
            Key <code>{extraction.key}</code>
            {extraction.description ? ` · ${extraction.description}` : ''}
          </p>
          <ExtractionSchedule extraction={extraction} />
        </div>
        <div className="extraction-runs-actions">
          {onRunNow && (
            <button type="button" className="btn-primary btn-small" disabled={starting || loading} onClick={() => setConfirmingRun(true)}>
              Run now
            </button>
          )}
          <button type="button" className="btn-secondary btn-small" onClick={load} disabled={loading}>
            Refresh
          </button>
        </div>
      </div>

      {onRunNow && confirmingRun && (
        <ConfirmRunModal
          extractionName={extraction.name}
          onClose={() => setConfirmingRun(false)}
          onConfirm={async () => {
            setStarting(true);
            setError(null);
            setNotice(null);
            try {
              const res = await onRunNow();
              // Straight to the new run: its file rows fill in as the transfers settle.
              setSelectedRunId(res.run_id);
            } finally {
              setStarting(false);
            }
          }}
        />
      )}

      {error && <div className="error-message">{error}</div>}
      {notice && <div className="success-message">{notice}</div>}
      {!ledgerConfigured && !error && (
        <div className="extraction-runs-notice">Run history is not available in this environment yet.</div>
      )}

      {loading ? (
        <div className="loading">Loading runs...</div>
      ) : (
        <table className="data-table extraction-runs-table">
          <thead>
            <tr>
              <th>Started</th>
              <th>Status</th>
              <th>Trigger</th>
              <th>Files</th>
              <th>Duration</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr>
                <td colSpan={5} className="empty-state">
                  No runs yet.
                </td>
              </tr>
            ) : (
              runs.map((run) => (
                <tr key={run.run_id} className="extraction-run-row" onClick={() => setSelectedRunId(run.run_id)}>
                  <td>
                    <button type="button" className="btn-link extraction-run-link">
                      {new Date(run.started_at).toLocaleString()}
                    </button>
                  </td>
                  <td>
                    <span className={`status-badge ${runStatusColor(run.status)}`}>{RUN_STATUS_LABEL[run.status]}</span>
                  </td>
                  <td>{run.trigger}</td>
                  <td className="extraction-run-counts">{summarizeRunCounts(run.counts)}</td>
                  <td>
                    <DurationBar run={run} maxMs={maxDurationMs} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

interface RunDetailProps {
  extraction: ExtractionSummary;
  runId: string;
  source: ExtractionRunsSource;
  onBack: () => void;
}

function RunDetail({ extraction, runId, source, onBack }: RunDetailProps) {
  const [run, setRun] = useState<ExtractionRun | null>(null);
  const [files, setFiles] = useState<ExtractionRunFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [truncated, setTruncated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setFiles([]);
      setTruncated(false);
      try {
        // Page through the file rows, rendering as they arrive.
        let cursor: string | undefined;
        let collected: ExtractionRunFile[] = [];
        do {
          const page = await source.getRun(runId, cursor);
          if (cancelled) return;
          setRun(page.run);
          collected = collected.concat(page.files);
          setFiles(collected);
          cursor = page.next_cursor ?? undefined;
          if (cursor && collected.length >= MAX_RENDERED_FILES) {
            setTruncated(true);
            break;
          }
        } while (cursor);
      } catch (err) {
        if (!cancelled) setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source, runId]);

  const tree = useMemo(() => buildRunFileTree(files), [files]);

  return (
    <div className="extraction-runs">
      <div className="extraction-runs-header">
        <div>
          <button type="button" className="btn-link" onClick={onBack}>
            &larr; All runs
          </button>
          <h3>
            {extraction.name} · run of {run ? new Date(run.started_at).toLocaleString() : '…'}
          </h3>
          {run && (
            <p className="extraction-runs-subtitle">
              <span className={`status-badge ${runStatusColor(run.status)}`}>{RUN_STATUS_LABEL[run.status]}</span>{' '}
              {run.trigger} · {summarizeRunCounts(run.counts)}
              {runDuration(run) ? ` · ${runDuration(run)}` : ''}
            </p>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {run?.error && <div className="error-message">{run.error}</div>}
      {truncated && (
        <div className="extraction-runs-notice">
          Showing the first {MAX_RENDERED_FILES.toLocaleString()} files of this run.
        </div>
      )}

      {loading && files.length === 0 ? (
        <div className="loading">Loading files...</div>
      ) : files.length === 0 ? (
        <div className="empty-state">No file was listed in this run.</div>
      ) : (
        <>
          {loading && <div className="loading">Loading more files…</div>}
          <table className="data-table extraction-files-table">
            <thead>
              <tr>
                <th>File</th>
                <th>Outcome</th>
                <th>Size</th>
                <th>Modified on server</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {tree.map((node) => (
                <TreeRows key={node.path} node={node} depth={0} />
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

/** Schedule and source, the two things every reader of a runs list wants next to it. */
export function ExtractionSchedule({ extraction }: { extraction: ExtractionSummary }) {
  return (
    <dl className="extraction-facts">
      <div>
        <dt>Schedule</dt>
        <dd>
          {extraction.enabled ? describeCron(extraction.schedule.run, extraction.schedule.timezone) : 'Paused'}
        </dd>
      </div>
      <div>
        <dt>Source</dt>
        <dd>
          <code>{extraction.host}{extraction.port && extraction.port !== 22 ? `:${extraction.port}` : ''}</code>
          {extraction.remote_paths.map((p) => (
            <code key={p} className="extraction-path">{p}</code>
          ))}
        </dd>
      </div>
    </dl>
  );
}

/**
 * Duration as a bar scaled to the longest run in the list, with the figure
 * beside it (always visible, small) and the exact value in the tooltip. A
 * running run shows a striped indeterminate bar.
 */
function DurationBar({ run, maxMs }: { run: ExtractionRun; maxMs: number }) {
  const ms = runDurationMs(run);
  if (ms === null) {
    return run.status === 'running' ? (
      <span className="duration-bar" title="Still running">
        <span className="duration-bar-track">
          <span className="duration-bar-fill running" />
        </span>
        <span className="duration-bar-label">in progress</span>
      </span>
    ) : (
      <span className="duration-bar-label">—</span>
    );
  }
  // Never below 4% so a sub-second run still shows a sliver next to a long one.
  const pct = maxMs > 0 ? Math.max(4, Math.round((ms / maxMs) * 100)) : 100;
  return (
    <span className="duration-bar" title={`${(ms / 1000).toFixed(1)} s`}>
      <span className="duration-bar-track">
        <span className="duration-bar-fill" style={{ width: `${pct}%` }} />
      </span>
      <span className="duration-bar-label">{runDuration(run)}</span>
    </span>
  );
}

function TreeRows({ node, depth }: { node: RunFileTreeNode; depth: number }) {
  const [open, setOpen] = useState(true);
  const indent = { paddingLeft: `${1 + depth * 1.25}rem` };

  if (!node.file) {
    const { total, failed } = countTreeFiles(node);
    return (
      <>
        <tr className="extraction-tree-folder">
          <td style={indent}>
            <button type="button" className="extraction-tree-toggle" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
              <span className="extraction-tree-caret">{open ? '▾' : '▸'}</span> {node.name}/
            </button>
          </td>
          <td colSpan={4} className="extraction-tree-folder-summary">
            {total} file{total === 1 ? '' : 's'}
            {failed ? ` · ${failed} failed` : ''}
          </td>
        </tr>
        {open && node.children.map((child) => <TreeRows key={child.path} node={child} depth={depth + 1} />)}
      </>
    );
  }

  const f = node.file;
  return (
    <tr className={f.outcome === 'failed' ? 'extraction-file-failed' : undefined}>
      <td style={indent} title={f.remote_path}>
        {node.name}
      </td>
      <td>
        <span className={`status-badge ${fileOutcomeColor(f.outcome)}`}>{FILE_OUTCOME_LABEL[f.outcome]}</span>
      </td>
      <td>{f.size === null ? '—' : formatFileSize(f.size)}</td>
      <td>{f.mtime ? new Date(f.mtime).toLocaleString() : '—'}</td>
      <td className="extraction-file-details">
        {f.error ? <span className="extraction-file-error">{f.error}</span> : f.reason ?? (f.s3_key ? 'Imported' : '')}
      </td>
    </tr>
  );
}
